import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { LoggerService } from '../../../../../services/logger-services/logger.service';
import { AgentApiService } from '../../../../../shared/services/agent-api/agent-api.service';
import { ApiService } from '../../../../../services/api/api.service';
import { TokenService } from '../../../../../shared/services/token/token.service';

/**
 * Single Share Conversation Service
 *
 * Handles generating a share link for a single conversation response (message).
 * This is the "single share" feature (share icon next to like/dislike buttons).
 *
 * Features:
 * - Dynamically checks if a `share_conversation` endpoint is configured for the agent
 * - Calls the agent-specific share API via AgentApiService/ApiService
 * - Returns the full shareable URL
 * - Returns placeholder when no endpoint is configured
 * - Works for any agent – just add `share_conversation` to api_endpoints.json
 */
const SHARE_CONVERSATION_KEY = 'share_conversation';
const VIA_AGENT_ID = 'via_agent';

export interface ShareConversationResult {
  status: 'success' | 'error';
  share_link: string;
}

export interface SharePopupData {
  share_link?: string | null;
  placeholder?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SingleShareConversationService {

  constructor(
    private readonly logger: LoggerService,
    private readonly agentApiService: AgentApiService,
    private readonly apiService: ApiService,
    private readonly tokenService: TokenService
  ) {}

  /**
   * Check whether a `share_conversation` endpoint is configured for the given agent.
   *
   * @param agentId - Agent identifier
   * @returns true if the endpoint exists in api_endpoints.json
   */
  hasShareEndpoint(agentId: string): boolean {
    return !!this.agentApiService.getApiUrlFromConfig(SHARE_CONVERSATION_KEY, agentId);
  }

  /**
   * Resolve share popup data for a single conversation response.
   *
   * Determines whether the agent has a configured share endpoint and a valid
   * response_id, then either calls the share API or returns placeholder data.
   *
   * @param agentId      - Agent identifier
   * @param tenantId     - Tenant identifier
   * @param responseId   - The pk_chathistoryid / response_id from the chat message
   * @param userId       - Optional user identifier (required for some agents like via_agent)
   * @returns Observable<SharePopupData> that emits the popup state
   */
  getSharePopupData(
    agentId: string,
    tenantId: string,
    responseId: string | number | null | undefined,
    userId?: string,
    threadId?: string
  ): Observable<SharePopupData> {
    const responseIdStr = responseId != null ? String(responseId) : '';
    const hasRealResponseId = responseIdStr !== '' && responseIdStr !== '0';
    const hasEndpoint = this.hasShareEndpoint(agentId);

    this.logger.logInfo(
      `[SingleShareConversation] getSharePopupData – agentId: ${agentId}, responseId: ${responseIdStr}, hasEndpoint: ${hasEndpoint}`,
      'getSharePopupData'
    );

    if (hasEndpoint && hasRealResponseId) {
      return this.callShareConversationApi(agentId, tenantId, responseIdStr, userId, threadId).pipe(
        map((result) => {
          if (result.status === 'success' && result.share_link) {
            return { share_link: result.share_link } as SharePopupData;
          }
          this.logger.logInfo(
            '[SingleShareConversation] Share API returned no link',
            'getSharePopupData'
          );
          return { share_link: null, placeholder: true } as SharePopupData;
        }),
        catchError((error) => {
          this.logger.logInfo(
            '[SingleShareConversation] Error calling share API',
            'getSharePopupData',
            error
          );
          return of({ share_link: null, placeholder: true } as SharePopupData);
        })
      );
    }

    // No share endpoint configured or no valid response_id – placeholder
    return of({ share_link: null, placeholder: true } as SharePopupData);
  }

  /**
   * Generic share conversation API call that works for any agent.
   *
   * Resolves the `share_conversation` endpoint and base URL from the
   * agent's api_endpoints.json config and constructs the full shareable URL.
   *
   * The request contract is chosen from config, not from the agent id:
   * - Endpoint resolved from the `common` block (RIA and VIA) → centralized API,
   *   identifies the turn by `message_id`
   * - Endpoint resolved from the agent block (regulations_agent) →
   *   agent API, identifies the turn by `response_id`
   *
   * @param agentId    - Agent ID to resolve endpoint config
   * @param tenantId   - Tenant identifier
   * @param responseId - Backend message id (pk_chathistoryid): the persisted
   *                     `message_id` for centralized agents, the stream
   *                     `response_id` for agent-level APIs
   * @param userId     - Optional user identifier (used by agent-owned share APIs)
   * @param threadId   - Optional thread identifier (used by agent-owned share APIs)
   */
  callShareConversationApi(
    agentId: string,
    tenantId: string,
    responseId: string,
    userId?: string,
    threadId?: string
  ): Observable<ShareConversationResult> {
    try {
      const endpoint = this.agentApiService.getApiUrlFromConfig(SHARE_CONVERSATION_KEY, agentId);
      if (!endpoint) {
        return of({ status: 'error', share_link: '' } as ShareConversationResult);
      }

      if (this.agentApiService.usesCommonEndpoint(SHARE_CONVERSATION_KEY, agentId)) {
        return this.shareViaCentralizedApi(endpoint, agentId, tenantId, responseId);
      }

      return this.shareViaAgentApi(endpoint, agentId, tenantId, responseId, userId, threadId);
    } catch (error: any) {
      this.logger.logError(
        `[SingleShareConversation] Share exception for ${agentId}: ${error?.message || error}`,
        'callShareConversationApi'
      );
      return of({ status: 'error', share_link: '' } as ShareConversationResult);
    }
  }

  /**
   * Share through the centralized agent-common API, which identifies the turn by
   * `message_id` and returns a JWT carrying that same `message_id` claim.
   */
  private shareViaCentralizedApi(
    endpoint: string,
    agentId: string,
    tenantId: string,
    messageId: string
  ): Observable<ShareConversationResult> {
    const centralBaseUrl = this.agentApiService.getBaseUrlForEndpoint(SHARE_CONVERSATION_KEY, agentId);

    this.logger.logInfo(
      `[SingleShareConversation] ${agentId} – calling ${endpoint} at ${centralBaseUrl}`,
      'callShareConversationApi'
    );

    return this.agentApiService
      .post<any>(endpoint, { tenant_id: tenantId, message_id: messageId }, undefined, centralBaseUrl)
      .pipe(
        map((response: any) => {
          const shareLinkId = response?.share_link || '';
          if (!shareLinkId) {
            return { status: 'error', share_link: '' } as ShareConversationResult;
          }
          const fullShareLink =
            `${window.location.origin}/agent-shared-chat/${shareLinkId}?tenant_id=${tenantId}&message_id=${messageId}&agent_id=${agentId}`;
          return { status: 'success', share_link: fullShareLink } as ShareConversationResult;
        }),
        catchError((error: any) => {
          this.logger.logError(
            `[SingleShareConversation] Share API error for ${agentId}: ${error?.message || error}`,
            'callShareConversationApi'
          );
          return of({ status: 'error', share_link: '' } as ShareConversationResult);
        })
      );
  }

  /**
   * Share through an agent-owned API (regulations_agent), which
   * identifies the turn by `response_id`.
   */
  private shareViaAgentApi(
    endpoint: string,
    agentId: string,
    tenantId: string,
    responseId: string,
    userId?: string,
    threadId?: string
  ): Observable<ShareConversationResult> {
    const customBaseUrl = this.agentApiService.getBaseUrlForAgent(agentId);
    const isViaAgent = agentId === VIA_AGENT_ID;

    const requestBody = isViaAgent
      ? { tenant_id: tenantId, user_id: userId || '', thread_id: threadId || '', response_id: responseId }
      : { tenant_id: tenantId, response_id: responseId };

    this.logger.logInfo(
      `[SingleShareConversation] ${agentId} – calling ${endpoint} at ${customBaseUrl}`,
      'callShareConversationApi'
    );

    return this.apiService
      .post<any>(endpoint, requestBody, this.tokenService.getAccessToken(), 'COMMON', customBaseUrl)
      .pipe(
        map((response: any) => {
          const shareLinkId = response?.share_link || '';
          if (!shareLinkId) {
            return { status: 'error', share_link: '' } as ShareConversationResult;
          }
          // Legacy Via share links use thread_id so the viewer routes to getSharedThread
          const fullShareLink = isViaAgent
            ? `${window.location.origin}/agent-shared-chat/${shareLinkId}?tenant_id=${tenantId}&thread_id=${threadId}&agent_id=${agentId}`
            : `${window.location.origin}/agent-shared-chat/${shareLinkId}?tenant_id=${tenantId}&response_id=${responseId}&agent_id=${agentId}`;

          this.logger.logInfo(
            `[SingleShareConversation] ${agentId} API call successful`,
            'callShareConversationApi'
          );
          return { status: 'success', share_link: fullShareLink } as ShareConversationResult;
        }),
        catchError((error: any) => {
          this.logger.logError(
            `[SingleShareConversation] ${agentId} API error: ${error?.message || error}`,
            'callShareConversationApi'
          );
          return of({ status: 'error', share_link: '' } as ShareConversationResult);
        })
      );
  }
}
